/* eslint-disable max-lines */
/* eslint-disable complexity */
import {
  Brain,
  Database,
  MessageSquare,
  Network,
  Target,
  User,
} from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import type { RCAAnalysis, RCAStep } from '@/lib/queries/rca/mockData';

import {
  formatDuration,
  formatTime,
  getRCAStatusBadge,
  getStatusIcon,
} from '../../-components/RCAUtils';

interface ChatMessage {
  id: string;
  content: string;
  sender: 'user' | 'annie';
  timestamp: string;
}

interface InvestigationTimelineProps {
  rca: RCAAnalysis;
  additionalChatMessages?: ChatMessage[];
}

interface TimelineEventProps {
  event: TimelineEvent;
  isLast: boolean;
}

type TimelineEvent =
  | (RCAStep & {
      type:
        | 'investigation_step'
        | 'slack_message'
        | 'data_fetch'
        | 'graph_walk'
        | 'incident_status';
    })
  | (ChatMessage & {
      type: 'chat_message';
      title: string;
      description: string;
      findings?: string;
      duration?: number;
      status?: RCAStep['status'];
    });

const getEventIcon = (
  type: TimelineEvent['type'],
  status?: RCAStep['status'],
  sender?: 'user' | 'annie',
): JSX.Element => {
  switch (type) {
    case 'investigation_step':
      return typeof status === 'string'
        ? getStatusIcon(status)
        : getStatusIcon('pending');
    case 'slack_message':
      return <MessageSquare className="w-5 h-5 text-blue-500" />;
    case 'data_fetch':
      return <Database className="w-5 h-5 text-purple-500" />;
    case 'graph_walk':
      return <Network className="w-5 h-5 text-orange-500" />;
    case 'incident_status':
      return <Target className="w-5 h-5 text-gray-500" />;
    case 'chat_message':
      return sender === 'user' ? (
        <User className="w-5 h-5 text-green-500" />
      ) : (
        <Brain className="w-5 h-5 text-purple-500" />
      );
    default:
      return getStatusIcon('pending');
  }
};

const TimelineEventComponent = ({
  event,
  isLast,
}: TimelineEventProps): JSX.Element => {
  const isChatMessage = event.type === 'chat_message';
  const chatEvent = isChatMessage
    ? (event as ChatMessage & {
        type: 'chat_message';
        title: string;
        description: string;
      })
    : null;

  return (
    <div className="flex gap-4 mb-6">
      <div className="flex flex-col items-center">
        <div className="flex-shrink-0">
          {getEventIcon(event.type, event.status, chatEvent?.sender)}
        </div>
        {!isLast && (
          <div className="w-px h-16 bg-gray-200 dark:bg-gray-700 mt-3" />
        )}
      </div>

      <div className="flex-1 max-w-3xl min-w-0">
        <div className="flex items-start gap-3 mb-2">
          <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-base flex-1">
            {isChatMessage
              ? `${chatEvent?.sender === 'user' ? 'User' : 'Annie'} Message`
              : event.title}
          </h4>
          <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
            {formatTime(event.timestamp)}
          </span>
        </div>

        <div
          className={`text-sm leading-relaxed mb-3 ${
            isChatMessage
              ? chatEvent?.sender === 'user'
                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-3 rounded-lg text-green-800 dark:text-green-200'
                : 'bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 p-3 rounded-lg text-purple-800 dark:text-purple-200'
              : 'text-gray-600 dark:text-gray-400'
          }`}
        >
          <div className={isChatMessage ? 'whitespace-pre-wrap' : ''}>
            {isChatMessage ? chatEvent?.content : event.description}
          </div>
        </div>

        {!isChatMessage &&
          typeof event.findings === 'string' &&
          event.findings.length > 0 && (
            <div className="bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg mb-2">
              <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">
                <span className="font-medium">Findings: </span>
                {event.findings}
              </p>
            </div>
          )}

        {!isChatMessage &&
          typeof event.duration === 'number' &&
          event.duration > 0 && (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Duration: {formatDuration(event.duration)}
            </div>
          )}
      </div>
    </div>
  );
};

export const InvestigationTimeline = ({
  rca,
  additionalChatMessages = [],
}: InvestigationTimelineProps): JSX.Element => {
  // Mock chat messages for this incident
  const mockChatMessages: ChatMessage[] = [
    {
      id: 'chat-1',
      content:
        'Can you provide more details about the database connection pool configuration?',
      sender: 'user',
      timestamp: '2024-12-15T10:40:00Z',
    },
    {
      id: 'chat-2',
      content:
        'The connection pool was configured with a maximum of 20 connections. Based on the logs, all connections were exhausted due to the slow query on the users table. I recommend adding an index on the email column and increasing the pool size to 50 connections as a temporary measure.',
      sender: 'annie',
      timestamp: '2024-12-15T10:40:30Z',
    },
    {
      id: 'chat-3',
      content: 'What was the impact on user experience during this incident?',
      sender: 'user',
      timestamp: '2024-12-15T10:41:00Z',
    },
    {
      id: 'chat-4',
      content:
        'Users experienced 95% authentication failure rate from 10:30 to 10:35 UTC. Approximately 1,200 login attempts failed during this 5-minute window. No data was lost, but users were unable to access their accounts.',
      sender: 'annie',
      timestamp: '2024-12-15T10:41:15Z',
    },
  ];

  // Convert RCA steps and chat messages to timeline events
  const investigationEvents: TimelineEvent[] = rca.steps.map(step => ({
    ...step,
    type: 'investigation_step' as const,
  }));

  const staticChatEvents: TimelineEvent[] = mockChatMessages.map(message => ({
    ...message,
    type: 'chat_message' as const,
    title: `${message.sender === 'user' ? 'User' : 'Annie'} Message`,
    description: message.content,
  }));

  const newChatEvents: TimelineEvent[] = additionalChatMessages.map(
    message => ({
      ...message,
      type: 'chat_message' as const,
      title: `${message.sender === 'user' ? 'User' : 'Annie'} Message`,
      description: message.content,
    }),
  );

  // Combine and sort all events by timestamp (most recent first)
  const timelineEvents: TimelineEvent[] = [
    ...investigationEvents,
    ...staticChatEvents,
    ...newChatEvents,
  ].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  );

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex-shrink-0 w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
          <Brain className="w-6 h-6 text-purple-600 dark:text-purple-400" />
        </div>
        <div className="flex-1">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Investigation Timeline
          </h3>
          <div className="flex items-center gap-3 mt-2">
            {getRCAStatusBadge(rca.status)}
            {rca.status === 'completed' &&
              typeof rca.confidence === 'number' &&
              rca.confidence > 0 && (
                <div className="flex items-center gap-1">
                  <Target className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {rca.confidence}% confidence
                  </span>
                </div>
              )}
          </div>
        </div>
      </div>

      {/* Root Cause Summary */}
      {rca.status === 'completed' &&
        typeof rca.rootCause === 'string' &&
        rca.rootCause.length > 0 && (
          <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <h4 className="font-semibold text-green-900 dark:text-green-100 text-base mb-2">
              <FormattedMessage id="rca.summary.title" />
            </h4>
            <p className="text-green-800 dark:text-green-200 text-sm leading-relaxed">
              {rca.rootCause}
            </p>
          </div>
        )}

      {/* Timeline */}
      <div className="flex-1">
        <div className="max-h-[60vh] overflow-y-auto pr-2">
          {timelineEvents.map((event, index) => (
            <TimelineEventComponent
              key={event.id}
              event={event}
              isLast={index === timelineEvents.length - 1}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
